import { useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, HelpCircle, Mail } from 'lucide-react';
import { AuthHeading, AuthLayout } from './AuthLayout';
import { Button } from '@/components/ui/Button';
import { Field, Input } from '@/components/ui/Field';
import { FormAlert, InlineFeedback } from '@/components/ui/Feedback';
import { api, ApiError } from '@/lib/api';

export function ForgotPasswordPage() {
  const [email, setEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const onSubmit = async (event: FormEvent) => {
    event.preventDefault();
    if (submitting) return;

    setSubmitting(true);
    setError(null);
    try {
      await api.post('/auth/forgot-password', { email: email.trim() }, { anonymous: true });
      // Deliberately unconditional: the response never reveals whether the
      // address is registered, so the UI must not either.
      setSent(true);
    } catch (caught) {
      // A rate limit is worth surfacing; everything else still reports success
      // so the endpoint cannot be used to enumerate accounts.
      if (caught instanceof ApiError && caught.status === 429) {
        setError('Too many requests. Please wait a few minutes and try again.');
      } else {
        setSent(true);
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <AuthLayout>
      <Link
        to="/login"
        className="mb-5 inline-flex items-center gap-2 text-sm font-semibold text-digital transition-colors hover:text-digital-600"
      >
        <ArrowLeft aria-hidden className="h-4 w-4" />
        Back to sign in
      </Link>

      {sent ? (
        <>
          <AuthHeading
            eyebrow="Check your inbox"
            title="Reset link sent"
            description="If an account matches that address, we've sent password reset instructions to it."
          />
          <InlineFeedback
            tone="success"
            title="Instructions on the way"
            description={`Sent to ${email}. The link expires in 60 minutes.`}
          />
          <hr className="my-6 border-line" />
          <p className="text-helper leading-relaxed text-muted">
            Didn't receive it? Check your spam folder, or{' '}
            <button
              type="button"
              onClick={() => setSent(false)}
              className="font-semibold text-digital transition-colors hover:text-digital-600"
            >
              try a different address
            </button>
            .
          </p>
        </>
      ) : (
        <>
          <AuthHeading
            eyebrow="Account recovery"
            title="Forgot your password?"
            description="Enter your work email and we will send password reset instructions if an account matches that address."
          />

          <form onSubmit={onSubmit} noValidate className="space-y-5">
            {error && <FormAlert>{error}</FormAlert>}

            <Field
              label="Work email"
              htmlFor="email"
              hint="Use the email address connected to your OneParacon account."
            >
              <Input
                id="email"
                name="email"
                type="email"
                autoComplete="username"
                required
                placeholder="name@paracon.com.au"
                leadingIcon={<Mail />}
                value={email}
                onChange={(event) => setEmail(event.target.value)}
              />
            </Field>

            <Button type="submit" size="lg" fullWidth loading={submitting}>
              {submitting ? 'Sending…' : 'Send reset link'}
            </Button>
          </form>

          <hr className="my-6 border-line" />

          <div className="flex items-start gap-3 rounded-lg bg-cloud px-4 py-3.5">
            <HelpCircle aria-hidden className="mt-px h-4 w-4 shrink-0 text-muted" />
            <p className="text-helper leading-relaxed text-muted">
              For security, OneParacon will not confirm whether an email address is registered.
            </p>
          </div>
        </>
      )}
    </AuthLayout>
  );
}
